import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams, useHistory, NavLink } from 'react-router-dom';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';

import LoadingSpinner from 'components/LoadingSpinner';
import jobsQueueActions from 'store/actions/jobsQueue_actions';
import { addNewNotification } from 'store/actions/notification_actions';
import {
  getExecutionAnalytics,
  updateErrorGroupCategory
} from 'utils/api.service';
import Cache from 'utils/Cache';

// Import report icons
import playwrightLogo from '../../assets/images/icons/playwright-logo.svg';
import allureLogo from '../../assets/images/icons/logo-report-sign.svg';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

// Initialize cache instance
const cache = new Cache();

const ExecutionDetails = ({ addNewNotification }) => {
  const { executionid, service } = useParams();
  const history = useHistory();

  const [execution, setExecution] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [aggregatedErrors, setAggregatedErrors] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const [errorFilter, setErrorFilter] = useState('');
  const [aiAnalytics, setAiAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('transactions');
  const [transactionFilter, setTransactionFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [chartView, setChartView] = useState('transactions'); // 'transactions' or 'iterations'
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [aiRetryCount, setAiRetryCount] = useState(0);
  const [topFailures, setTopFailures] = useState([]);
  const [updatingCategories, setUpdatingCategories] = useState(new Set());
  const [aiAnalyticsStatus, setAiAnalyticsStatus] = useState(null); // 'no-failures', 'error', 'success', 'polling'
  const [isUsingCachedData, setIsUsingCachedData] = useState(false);
  const [pollingExecutionId, setPollingExecutionId] = useState(null);
  const [pollingStartTime, setPollingStartTime] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [pollingTimer, setPollingTimer] = useState(null);
  const [pollingElapsedSeconds, setPollingElapsedSeconds] = useState(0);
  const [isPollingInProgress, setIsPollingInProgress] = useState(false);

  // Predefined error categories for dropdown
  const errorCategories = [
    'Script Error',
    'Application Error',
    'Environment Error',
    'Data Error'
  ];

  // Function to map AI categories to our predefined categories
  const mapAICategoryToPredefined = (aiCategory) => {
    if (!aiCategory) return null;

    const categoryLower = aiCategory.toLowerCase();

    // Check for partial matches and common variations
    if (categoryLower.includes('script')) return 'Script Error';
    if (categoryLower.includes('application') || categoryLower.includes('app')) return 'Application Error';
    if (categoryLower.includes('environment') || categoryLower.includes('env')) return 'Environment Error';
    if (categoryLower.includes('data')) return 'Data Error';

    // If no match found, return null
    return null;
  };

  useEffect(() => {
    fetchExecutionDetails();
  }, [executionid, service]);

  // Cleanup polling interval and timer on component unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      if (pollingTimer) {
        clearInterval(pollingTimer);
      }
    };
  }, [pollingInterval, pollingTimer]);

  // Update top failures when transactions change
  useEffect(() => {
    if (transactions.length > 0) {
      const failures = generateTopFailures();
      setTopFailures(failures);
    }
  }, [transactions]);

  // Generate cache key for AI analytics based on execution ID and failed transactions
  const generateAICacheKey = (failedTransactions) => {
    // Create a simple hash of failed transaction IDs and messages to detect changes
    const transactionData = failedTransactions.map(t => ({
      id: t.transactionid,
      status: t.status,
      message: t.message?.substring(0, 100) // Use first 100 chars for efficiency
    }));

    const dataString = JSON.stringify(transactionData);
    const hash = btoa(dataString).substring(0, 16); // Simple hash

    return `ai-analytics-${executionid}-${hash}`;
  };

  const fetchExecutionDetails = async () => {
    try {
      setIsLoading(true);

      let response = await jobsQueueActions.getExecutionDetails('hosting', executionid);
      if (response && response.data) {
        setExecution(response.data);
        const transactionData = response.data.items || [];
        setTransactions(transactionData);

        // Aggregate similar error messages
        const aggregated = aggregateErrorMessages(transactionData);
        setAggregatedErrors(aggregated);

        // Fetch analytics
        // await fetchAnalytics(transactionData); // Removed as per new logic
      } else {
        throw new Error('No execution data received');
      }
    } catch (error) {
      console.error('Error fetching execution details:', error);
      const errorMessage = 'Failed to fetch execution details.';
      addNewNotification(errorMessage, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const aggregateErrorMessages = (transactions) => {
    const errorGroups = {};

    transactions.forEach(transaction => {
      if (transaction.status === 'failed' && transaction.message) {
        const message = transaction.message.trim();

        if (!errorGroups[message]) {
          errorGroups[message] = [];
        }

        errorGroups[message].push(transaction);
      }
    });

    return Object.entries(errorGroups).map(([message, transactions]) => ({
      message,
      transactions,
      count: transactions.length
    })).sort((a, b) => b.count - a.count);
  };

  // New function to handle AI Analysis tab click
  const handleAIAnalysisTabClick = async () => {
    setActiveTab('ai-analysis');

    if (!aiAnalytics && !isLoadingAI) {
      await fetchAIAnalytics();
    }
  };

  // Function to force refresh AI analytics (bypass cache)
  const handleRefreshAIAnalytics = async () => {
    // Clear cache first
    const failedTransactions = transactions.filter(t => t.status === 'failed');
    const cacheKey = generateAICacheKey(failedTransactions);
    cache.delete(cacheKey);

    // Clear any existing polling
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    if (pollingTimer) {
      clearInterval(pollingTimer);
      setPollingTimer(null);
    }

    // Reset elapsed seconds and polling state
    setPollingElapsedSeconds(0);
    setIsPollingInProgress(false);

    // Force fresh fetch
    setIsUsingCachedData(false);
    await fetchAIAnalytics();
  };

  // Function to regenerate AI analysis with fresh AI processing
  const handleRegenerateAIAnalytics = async () => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      "⚠️ Regenerate AI Analysis\n\n" +
      "This will completely replace the current analysis with fresh AI processing. " +
      "Any insights or categorizations from the current analysis will be lost.\n\n" +
      "Are you sure you want to continue?"
    );

    if (!confirmed) {
      return; // User cancelled
    }

    // Clear cache first
    const failedTransactions = transactions.filter(t => t.status === 'failed');
    const cacheKey = generateAICacheKey(failedTransactions);
    cache.delete(cacheKey);

    // Clear any existing polling
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    if (pollingTimer) {
      clearInterval(pollingTimer);
      setPollingTimer(null);
    }

    // Reset elapsed seconds and polling state
    setPollingElapsedSeconds(0);
    setIsPollingInProgress(false);

    // Force fresh fetch with regenerate flag
    setIsUsingCachedData(false);
    await fetchAIAnalytics(true); // Pass regenerate flag
  };

  // Helper function to poll for analytics status
  const pollAnalyticsStatus = async (executionId) => {
    // Prevent concurrent polling calls
    if (isPollingInProgress) {
      console.log(`[${new Date().toLocaleTimeString()}] Polling already in progress, skipping`);
      return false;
    }

    try {
      setIsPollingInProgress(true);
      console.log(`[${new Date().toLocaleTimeString()}] Polling status for execution ID: ${executionId}`);
      const response = await getExecutionAnalytics({ executionid: executionId, checkStatus: true });
      console.log(`[${new Date().toLocaleTimeString()}] Poll response:`, response.data);

      if (response.data.status === 'completed') {
        // Analysis completed, we have the data
        setAiAnalytics(response.data.data);
        setAiAnalyticsStatus('success');
        setIsLoadingAI(false);

        // Cache the result
        const failedTransactions = transactions.filter(t => t.status === 'failed');
        const cacheKey = generateAICacheKey(failedTransactions);
        cache.set(cacheKey, response.data.data, 1800000); // 30 minutes

        // Clear polling
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        if (pollingTimer) {
          clearInterval(pollingTimer);
          setPollingTimer(null);
        }
        setPollingElapsedSeconds(0);
        setIsPollingInProgress(false);

        return true; // Completed
      } else if (response.data.status === 'failed') {
        // Analysis failed
        setAiAnalyticsStatus('error');
        setIsLoadingAI(false);

        // Clear polling
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        if (pollingTimer) {
          clearInterval(pollingTimer);
          setPollingTimer(null);
        }
        setPollingElapsedSeconds(0);
        setIsPollingInProgress(false);

        addNewNotification(
          response.data.error || 'AI analysis failed. Please try again.',
          'danger'
        );

        return true; // Failed (completed polling)
      }

      // Still in progress
      setIsPollingInProgress(false);
      return false;
    } catch (error) {
      console.error('Error polling analytics status:', error);
      console.error('Poll error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      // Don't stop polling on individual API errors - let the main timeout logic handle it
      // Just continue polling and let the main interval handle the timeout
      setIsPollingInProgress(false);
      return false; // Continue polling
    }
  };

  // Modified AI analytics fetch with async polling approach
  const fetchAIAnalytics = async (regenerateAI = false) => {
    try {
      setIsLoadingAI(true);
      setAiAnalyticsStatus('polling');

      // Filter to only send failed transactions (exclude resubmitted and success)
      const failedTransactions = transactions.filter(transaction =>
        transaction.status === 'failed'
      );

      // If no failed transactions, don't make API call
      if (failedTransactions.length === 0) {
        console.log('No failed transactions found, skipping AI analysis');
        setAiAnalytics(null);
        setAiAnalyticsStatus('no-failures');
        setIsLoadingAI(false);
        return;
      }

      // Generate cache key
      const cacheKey = generateAICacheKey(failedTransactions);

      // Check cache first
      const cachedResult = cache.get(cacheKey);
      if (cachedResult) {
        console.log('Using cached AI analytics data');
        setAiAnalytics(cachedResult);
        setIsUsingCachedData(true);
        setAiAnalyticsStatus('success');
        setIsLoadingAI(false);
        return;
      }

      // Format transactions with only required fields (exact format as specified)
      const formattedTransactions = failedTransactions.map(transaction => ({
        executionid: executionid,
        transactionid: transaction.transactionid,
        status: transaction.status,
        name: transaction.name || transaction.transactionname || '',
        message: transaction.message || '',
        logid: transaction.logid || transaction.transactionid,
        steps: transaction.steps || [],
        iterations: transaction.iterations || [],
        tools: transaction.tools || 'playwright'
      }));

      const analyticsData = {
        executionid: executionid,
        service: service,
        org: service || 'hosting',
        workstream: 'testing',
        transactions: formattedTransactions,
        ...(regenerateAI && { regenerateai: true })
      };

      console.log(`Sending ${failedTransactions.length} failed transactions to AI Analytics API`);
      console.log('Request payload:', analyticsData);

      const response = await getExecutionAnalytics(analyticsData);
      console.log('Raw API Response:', response);
      console.log('API Response Data:', response.data);

      // Check if we got an immediate response with data (backward compatibility)
      if (response.data && response.data.executionAnalysis) {
        // Old API format - immediate response
        console.log('Received immediate response with executionAnalysis');
        cache.set(cacheKey, response.data, 1800000);
        console.log('AI analytics data cached for 30 minutes');

        setAiAnalytics(response.data);
        setAiAnalyticsStatus('success');
        setIsUsingCachedData(false);
        setIsLoadingAI(false);
        return;
      }

      // Check if we got an immediate completed response (new format)
      if (response.data && response.data.status === 'completed' && response.data.data) {
        // New API format - immediate completed response
        console.log('Received immediate completed response with data');
        cache.set(cacheKey, response.data.data, 1800000);
        console.log('AI analytics data cached for 30 minutes');

        setAiAnalytics(response.data.data);
        setAiAnalyticsStatus('success');
        setIsUsingCachedData(false);
        setIsLoadingAI(false);
        return;
      }

      // New async API format - start polling
      // Check for both executionId and executionid (case variations)
      const executionId = response.data?.executionId || response.data?.executionid;
      if (response.data && executionId) {
        const analyticsExecutionId = executionId;
        setPollingExecutionId(analyticsExecutionId);
        setPollingStartTime(Date.now());

        console.log(`Starting polling for analytics execution ID: ${analyticsExecutionId}`);

        // Start polling every 5 seconds
        const startTime = Date.now(); // Capture start time in closure
        const intervalId = setInterval(async () => {
          // Check for timeout before polling
          const now = Date.now();
          const maxPollingTime = 120000; // 120 seconds

          if (now - startTime > maxPollingTime) {
            // Timeout - stop polling and show error
            setAiAnalyticsStatus('error');
            setIsLoadingAI(false);

            clearInterval(intervalId);
            setPollingInterval(null);
            if (pollingTimer) {
              clearInterval(pollingTimer);
              setPollingTimer(null);
            }
            setPollingElapsedSeconds(0);

            addNewNotification(
              'AI analysis is still in progress after 120 seconds. Please try again.',
              'warning'
            );
            return;
          }

          const isCompleted = await pollAnalyticsStatus(analyticsExecutionId);
          if (isCompleted) {
            clearInterval(intervalId);
            setPollingInterval(null);
            if (pollingTimer) {
              clearInterval(pollingTimer);
              setPollingTimer(null);
            }
            setPollingElapsedSeconds(0);
          }
        }, 5000); // Poll every 5 seconds

        setPollingInterval(intervalId);

        // Start a timer to update the display every second
        setPollingElapsedSeconds(0);
        const timerId = setInterval(() => {
          setPollingElapsedSeconds(prev => prev + 1);
        }, 1000);
        setPollingTimer(timerId);

        // Note: Removed the setTimeout duplicate polling call to avoid race conditions
        // The setInterval above will handle all polling
      } else {
        console.error('Invalid API response format. Response:', response.data);
        console.error('Expected either executionAnalysis (old format) or executionId/executionid (new format)');
        throw new Error('Invalid API response format - missing executionAnalysis or executionId');
      }

    } catch (error) {
      console.error('Analytics API error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      setAiAnalytics(null);
      setAiAnalyticsStatus('error');
      setIsLoadingAI(false);

      const errorMessage = error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to start AI analysis. Please try again.';

      addNewNotification(errorMessage, 'danger');
    }
  };

  const generateLocalAnalytics = (transactions) => {
    const failedTransactions = transactions.filter(t => t.status === 'failed');
    const totalTransactions = transactions.length;
    const failureRate = ((failedTransactions.length / totalTransactions) * 100).toFixed(1);

    // Analyze error messages for common patterns
    const errorPatterns = {};
    failedTransactions.forEach(transaction => {
      const message = transaction.message?.toLowerCase() || '';
      if (message.includes('timeout') || message.includes('connection')) {
        errorPatterns.connectivity = (errorPatterns.connectivity || 0) + 1;
      }
      if (message.includes('auth') || message.includes('login') || message.includes('credential')) {
        errorPatterns.authentication = (errorPatterns.authentication || 0) + 1;
      }
      if (message.includes('environment') || message.includes('env') || message.includes('server')) {
        errorPatterns.infrastructure = (errorPatterns.infrastructure || 0) + 1;
      }
      if (message.includes('data') || message.includes('validation')) {
        errorPatterns.data = (errorPatterns.data || 0) + 1;
      }
    });

    const insights = [];
    const recommendations = [];

    if (failureRate > 50) {
      insights.push(`High failure rate detected: ${failureRate}% of transactions failed`);
      recommendations.push('Review test environment stability and data quality');
    }

    if (errorPatterns.connectivity) {
      insights.push(`${errorPatterns.connectivity} connectivity-related failures detected`);
      recommendations.push('Check network connectivity and timeout configurations');
    }

    if (errorPatterns.authentication) {
      insights.push(`${errorPatterns.authentication} authentication-related failures detected`);
      recommendations.push('Verify authentication credentials and service availability');
    }

    if (errorPatterns.infrastructure) {
      insights.push(`${errorPatterns.infrastructure} infrastructure-related failures detected`);
      recommendations.push('Check environment status and server health');
    }

    if (errorPatterns.data) {
      insights.push(`${errorPatterns.data} data-related failures detected`);
      recommendations.push('Review test data quality and validation rules');
    }

    let summary = `Execution analysis: ${failureRate}% failure rate across ${totalTransactions} transactions.`;

    const topErrorType = Object.entries(errorPatterns).sort(([, a], [, b]) => b - a)[0];
    if (topErrorType) {
      summary += ` Primary failure category: ${topErrorType[0]} (${topErrorType[1]} instances).`;
    }

    return {
      summary,
      insights: insights.length > 0 ? insights : ['No specific patterns detected in failure analysis'],
      recommendations: recommendations.length > 0 ? recommendations : ['Monitor execution patterns for future analysis']
    };
  };

  // Filter transactions to only include failed and success
  const getFilteredTransactions = () => {
    return transactions.filter(t => t.status === 'failed' || t.status === 'success');
  };

  // Update status counts to exclude resubmitted
  const getStatusCounts = () => {
    const filteredTransactions = getFilteredTransactions();
    const counts = {};

    filteredTransactions.forEach(transaction => {
      const status = transaction.status;
      counts[status] = (counts[status] || 0) + 1;
    });

    return counts;
  };

  // Update total transactions count
  const getTotalTransactions = () => {
    return getFilteredTransactions().length;
  };

  // Get iteration status counts across all transactions
  const getIterationStatusCounts = () => {
    const counts = {};
    
    transactions.forEach(transaction => {
      if (transaction.iterations && Array.isArray(transaction.iterations)) {
        transaction.iterations.forEach(iteration => {
          const status = iteration.iterationStatus;
          if (status) {
            counts[status] = (counts[status] || 0) + 1;
          }
        });
      }
    });
    
    return counts;
  };

  // Get total iterations count
  const getTotalIterations = () => {
    let total = 0;
    transactions.forEach(transaction => {
      if (transaction.iterations && Array.isArray(transaction.iterations)) {
        total += transaction.iterations.length;
      }
    });
    return total;
  };

  // Enhanced function to extract and group similar error messages
  const generateTopFailures = (transactions) => {
    // Only consider failed transactions (already filtered)
    const filteredTransactions = getFilteredTransactions();
    const failedTransactions = filteredTransactions.filter(t => t.status === 'failed');

    if (failedTransactions.length === 0) {
      return [];
    }

    // Extract meaningful error messages
    const extractErrorMessage = (message) => {
      if (!message) return 'Unknown error';

      // Remove the common prefix "Test execution is failed. Errors: "
      let cleanMessage = message.replace(/^Test execution is failed\.\s*Errors:\s*"?/, '');

      // Remove quotes and extra formatting
      cleanMessage = cleanMessage.replace(/"/g, '').trim();

      // Extract the main error part - take first 8-10 words for grouping
      const words = cleanMessage.split(/\s+/);
      const keyWords = words.slice(0, 8).join(' ');

      return {
        groupKey: keyWords,
        fullMessage: cleanMessage,
        originalMessage: message
      };
    };

    // Group similar errors with improved logic
    const errorGroups = {};

    failedTransactions.forEach(transaction => {
      const errorData = extractErrorMessage(transaction.message);
      const groupKey = errorData.groupKey;

      // Find similar existing group (60% similarity threshold - more lenient)
      let matchedGroupKey = null;
      for (const existingKey of Object.keys(errorGroups)) {
        if (calculateSimilarity(groupKey, existingKey) > 0.6) {
          matchedGroupKey = existingKey;
          break;
        }
      }

      const finalGroupKey = matchedGroupKey || groupKey;

      if (!errorGroups[finalGroupKey]) {
        errorGroups[finalGroupKey] = {
          message: errorData.fullMessage,
          originalMessage: errorData.originalMessage,
          displayMessage: errorData.fullMessage.length > 120 ?
            `${errorData.fullMessage.substring(0, 120)}...` :
            errorData.fullMessage,
          transactions: [],
          count: 0
        };
      }

      errorGroups[finalGroupKey].transactions.push(transaction);
      errorGroups[finalGroupKey].count++;
    });

    // Convert to array and sort by count - show all groups
    return Object.values(errorGroups)
      .sort((a, b) => b.count - a.count);
  };

  // Improved similarity calculation
  const calculateSimilarity = (str1, str2) => {
    const words1 = str1.toLowerCase().split(/\s+/);
    const words2 = str2.toLowerCase().split(/\s+/);

    // Check for common key terms
    const commonWords = words1.filter(word =>
      word.length > 3 && words2.includes(word) // Only consider words longer than 3 chars
    );

    const totalUniqueWords = new Set([...words1, ...words2]).size;

    return commonWords.length / Math.min(words1.length, words2.length);
  };

  // Enhanced pie chart data with numbers (excluding resubmitted)
  const getPieChartData = () => {
    const statusCounts = getStatusCounts();

    // Define color mapping for each status
    const statusColors = {
      'failed': '#dc3545',     // red
      'success': '#28a745',    // green
      'in-progress': '#ffc107', // yellow
      'running': '#17a2b8',    // blue
      'queued': '#6c757d',     // gray
      'submitted': '#fd7e14'   // orange
    };

    // Create arrays in the same order as the status keys
    const statuses = Object.keys(statusCounts);
    const colors = statuses.map(status => statusColors[status] || '#6c757d');

    return {
      labels: statuses.map(status =>
        `${status} (${statusCounts[status]})`
      ),
      datasets: [
        {
          data: Object.values(statusCounts),
          backgroundColor: colors,
          borderWidth: 2,
          borderColor: '#fff',
        },
      ],
    };
  };

  // Pie chart data for iterations
  const getIterationPieChartData = () => {
    const iterationCounts = getIterationStatusCounts();

    // Define color mapping for iteration statuses
    const statusColors = {
      'success': '#28a745',    // green
      'failed': '#dc3545',     // red
      'skipped': '#9CA3AF',    // yellow/amber
      'pending': '#6c757d',    // gray
    };

    // Create arrays in the same order as the status keys
    const statuses = Object.keys(iterationCounts);
    const colors = statuses.map(status => statusColors[status] || '#6c757d');

    return {
      labels: statuses.map(status =>
        `${status} (${iterationCounts[status]})`
      ),
      datasets: [
        {
          data: Object.values(iterationCounts),
          backgroundColor: colors,
          borderWidth: 2,
          borderColor: '#fff',
        },
      ],
    };
  };

  // Update handleTransactionClick to use correct URL format
  const handleTransactionClick = (transactionId) => {
    const url = `/view/${service}/${executionid}?transactionid=${transactionId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Handle error category update
  const handleCategoryUpdate = async (groupId, newCategory, oldCategory, realErrorMessage, errorPattern) => {
    if (newCategory === oldCategory) return; // No change

    try {
      // Add to updating set to show loading state
      setUpdatingCategories(prev => new Set(prev).add(groupId));

      // Extract org and workstream from service and execution context
      const org = service; // Use service as org
      const workstream = 'testing'; // Default workstream or derive from context

      // Combine realErrorMessage and errorPattern
      const combinedErrorMessage = `${realErrorMessage || ''} ${errorPattern || ''}`.trim();

      const updateData = {
        realErrorMessage: combinedErrorMessage || 'Error message not available',
        errorCategory: newCategory,
        executionid: executionid,
        groupid: groupId
      };

      await updateErrorGroupCategory(org, workstream, updateData);

      // Clear cache to ensure fresh data on next load (user feedback might improve AI)
      const failedTransactions = transactions.filter(t => t.status === 'failed');
      const cacheKey = generateAICacheKey(failedTransactions);
      cache.delete(cacheKey);
      console.log('AI analytics cache cleared due to category update');

      // Update local state to reflect the change
      setAiAnalytics(prevAnalytics => {
        if (!prevAnalytics?.executionAnalysis?.errorGroups) return prevAnalytics;

        const updatedAnalytics = { ...prevAnalytics };
        const updatedGroups = updatedAnalytics.executionAnalysis.errorGroups.map(group =>
          group.groupId === groupId
            ? { ...group, errorCategory: newCategory }
            : group
        );
        updatedAnalytics.executionAnalysis.errorGroups = updatedGroups;
        return updatedAnalytics;
      });

      addNewNotification(
        `Error category updated successfully from "${oldCategory}" to "${newCategory}"`,
        'success'
      );

    } catch (error) {
      console.error('Failed to update error category:', error);
      addNewNotification(
        'Failed to update error category. Please try again.',
        'danger'
      );
    } finally {
      // Remove from updating set
      setUpdatingCategories(prev => {
        const newSet = new Set(prev);
        newSet.delete(groupId);
        return newSet;
      });
    }
  };

  // Function to render report icons for playwright executions
  const renderExecutionReports = () => {
    if (!execution || execution.tools !== 'playwright') {
      return null;
    }

    // Check if execution has reports available (similar logic to JobsQueue)
    const hasPlaywrightReports = execution.totalcount > 1 &&
      (['failed', 'success'].includes(execution.overall_status) ||
        ['failed', 'success'].includes(execution.status));

    // For testing - temporarily force Allure to show
    const hasAllureReports = execution.isAllureConfigured && // Force show for testing
      execution.totalcount > 1 &&
      (['failed', 'success'].includes(execution.overall_status) ||
        ['failed', 'success'].includes(execution.status));

    // Debug logging to see why Allure is not showing
    console.log('Execution object for reports:', {
      executionid: execution.executionid,
      tools: execution.tools,
      totalcount: execution.totalcount,
      isAllureConfigured: execution.isAllureConfigured,
      overall_status: execution.overall_status,
      status: execution.status,
      hasPlaywrightReports,
      hasAllureReports
    });

    if (!hasPlaywrightReports && !hasAllureReports) {
      return null;
    }

    return (
      <div className="execution-reports-section">
        <span className="reports-label"><strong>Reports:</strong></span>
        <div className="execution-reports-icons">
          {hasPlaywrightReports && (
            <a
              href={`/playwright/report/${executionid}`}
              target="_blank"
              rel="noopener noreferrer"
              title="Playwright Report"
              className="execution-report-icon-item"
            >
              <img src={playwrightLogo} alt="Playwright Report" />
              <span>Playwright</span>
            </a>
          )}
          {hasAllureReports && (
            <a
              href={`/playwright/report/${executionid}?reportType=allure`}
              target="_blank"
              rel="noopener noreferrer"
              title="Allure Report"
              className="execution-report-icon-item"
            >
              <img src={allureLogo} alt="Allure Report" />
              <span>Allure</span>
            </a>
          )}
        </div>
      </div>
    );
  };

  // Fixed sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Update getSortedTransactions to work with filtered transactions
  const getSortedTransactions = () => {
    const filteredTransactions = getFilteredTransactions();
    let sortedTransactions = [...filteredTransactions];

    if (sortConfig.key) {
      sortedTransactions.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'status') {
          // Priority order: failed first, then success
          const statusOrder = { 'failed': 1, 'success': 2 };
          aValue = statusOrder[aValue] || 999;
          bValue = statusOrder[bValue] || 999;
        } else if (sortConfig.key === 'createddate') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        } else if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortedTransactions;
  };

  // Update getFilteredAndSortedTransactions to work with already filtered data
  const getFilteredAndSortedTransactions = () => {
    const sorted = getSortedTransactions();

    if (!transactionFilter) {
      return sorted;
    }

    return sorted.filter(transaction =>
      transaction.transactionid.toLowerCase().includes(transactionFilter.toLowerCase()) ||
      transaction.status.toLowerCase().includes(transactionFilter.toLowerCase()) ||
      (transaction.name && transaction.name.toLowerCase().includes(transactionFilter.toLowerCase())) ||
      (transaction.message && transaction.message.toLowerCase().includes(transactionFilter.toLowerCase()))
    );
  };

  const renderTransactionDetailsTab = () => {
    const filteredTransactions = getFilteredTransactions();

    if (!filteredTransactions || filteredTransactions.length === 0) {
      return <p>No transactions found for this execution.</p>;
    }

    const sortedAndFilteredTransactions = getFilteredAndSortedTransactions();

    return (
      <div className="transaction-details-tab">
        {/* Search and Controls */}
        <div className="transaction-controls mb-3">
          <div className="row align-items-center">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Search transactions by ID, name, status, or error message..."
                value={transactionFilter}
                onChange={(e) => setTransactionFilter(e.target.value)}
              />
            </div>
            <div className="col-md-6 text-right">
              <span className="text-muted">
                Showing {sortedAndFilteredTransactions.length} of {filteredTransactions.length} transactions
              </span>
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort('transactionid')}
                  style={{ cursor: 'pointer' }}
                  className="sortable-header"
                >
                  Transaction ID
                  {sortConfig.key === 'transactionid' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => handleSort('name')}
                  style={{ cursor: 'pointer' }}
                  className="sortable-header"
                >
                  Name
                  {sortConfig.key === 'name' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => handleSort('status')}
                  style={{ cursor: 'pointer' }}
                  className="sortable-header"
                >
                  Status
                  {sortConfig.key === 'status' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => handleSort('createddate')}
                  style={{ cursor: 'pointer' }}
                  className="sortable-header"
                >
                  Created Date
                  {sortConfig.key === 'createddate' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </th>
                <th>Error Message</th>
              </tr>
            </thead>
            <tbody>
              {sortedAndFilteredTransactions.map((transaction) => (
                <tr key={transaction.transactionid}>
                  <td>
                    <NavLink
                      to={`/view/${service}/${executionid}?transactionid=${transaction.transactionid}`}
                      className="transaction-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {transaction.transactionid}
                    </NavLink>
                  </td>
                  <td>
                    {transaction.name || 'N/A'}
                  </td>
                  <td>
                    <span className={`status-badge status-${transaction.status}`}>
                      {transaction.status}
                    </span>
                  </td>
                  <td className="created-date-cell">
                    {transaction.createddate ?
                      new Date(transaction.createddate).toLocaleString() :
                      'N/A'
                    }
                  </td>
                  <td className="error-message-cell">
                    {transaction.status === 'failed' ?
                      <div className="error-message-text">
                        {transaction.message}
                      </div>
                      : 'N/A'
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderAIAnalysisTab = () => {
    if (isLoadingAI) {
      return (
        <div className="ai-analysis-loading">
          <div className="text-center py-5">
            <LoadingSpinner />
            {aiAnalyticsStatus === 'polling' ? (
              <>
                <h5 className="mt-3">AI Analysis in Progress...</h5>
                <p className="text-muted">
                  Analysis will take some time, please wait. We're processing your execution data.
                </p>
                <p className="text-muted small">
                  Running for {pollingElapsedSeconds} seconds
                  {pollingExecutionId && (
                    <span className="d-block mt-1">
                      Execution ID: <code>{pollingExecutionId}</code>
                    </span>
                  )}
                </p>
              </>
            ) : (
              <>
                <h5 className="mt-3">Starting AI Analysis...</h5>
                <p className="text-muted">Initializing analysis of your execution data</p>
              </>
            )}
          </div>
        </div>
      );
    }

    if (!aiAnalytics || !aiAnalytics.executionAnalysis) {
      // Show different messages based on the reason
      if (aiAnalyticsStatus === 'no-failures') {
        return (
          <div className="ai-analysis-placeholder">
            <div className="text-center py-5">
              <i className="bi bi-check-circle" style={{ fontSize: '48px', color: '#28a745' }}></i>
              <h5 className="mt-3">No Failures to Analyze</h5>
              <p className="text-muted mb-3">
                All transactions completed successfully. There are no failures for AI to analyze.
              </p>
            </div>
          </div>
        );
      } else if (aiAnalyticsStatus === 'error') {
        // Show error message with retry button only when status is 'error'
        return (
          <div className="ai-analysis-placeholder">
            <div className="text-center py-5">
              <i className="bi bi-exclamation-triangle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
              <h5 className="mt-3">AI Analysis Failed</h5>
              <p className="text-muted mb-3">
                The AI analysis failed or timed out. Please try again.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => fetchAIAnalytics()}
                disabled={isLoadingAI}
              >
                {isLoadingAI ? 'Retrying...' : 'Retry Analysis'}
              </button>
            </div>
          </div>
        );
      } else {
        // Default message for other states (like initial load)
        return (
          <div className="ai-analysis-placeholder">
            <div className="text-center py-5">
              <i className="bi bi-robot" style={{ fontSize: '48px', color: '#6c757d' }}></i>
              <h5 className="mt-3">AI Analysis Not Available</h5>
              <p className="text-muted mb-3">
                Click "AI Analysis" tab to start analyzing your execution data.
              </p>
            </div>
          </div>
        );
      }
    }

    const { executionAnalysis } = aiAnalytics;
    const { errorGroups, errorCategory } = executionAnalysis;

    // Debug logging to check what data we're actually receiving
    console.log(`[${new Date().toLocaleTimeString()}] AI Analytics Full Response:`, aiAnalytics);
    console.log(`[${new Date().toLocaleTimeString()}] Error Category Object:`, errorCategory);
    console.log(`[${new Date().toLocaleTimeString()}] Error Category Values:`, {
      scriptErrorCount: errorCategory?.scriptErrorCount,
      applicationErrorCount: errorCategory?.applicationErrorCount,
      environmentErrorCount: errorCategory?.environmentErrorCount,
      dataErrorCount: errorCategory?.dataErrorCount
    });

    // Check if error categories match your expected values
    const expectedValues = {
      scriptErrorCount: 10,
      applicationErrorCount: 0,
      environmentErrorCount: 0,
      dataErrorCount: 1
    };

    console.log('Expected vs Actual comparison:', Object.keys(expectedValues).map(key => ({
      field: key,
      expected: expectedValues[key],
      actual: errorCategory?.[key],
      match: expectedValues[key] === errorCategory?.[key]
    })));

    return (
      <div className="ai-analysis-tab">
        {/* AI Analysis Disclaimer */}
        <div className="ai-disclaimer-section">
          <div className="ai-disclaimer">
            <i className="bi bi-info-circle"></i>
            <div className="disclaimer-content">
              <strong>AI-Generated Analysis</strong>
              <p>This analysis is generated by artificial intelligence and may not be 100% accurate. Please use this information as a starting point and verify findings with your own investigation.</p>
            </div>
          </div>
        </div>

        {/* Error Statistics */}
        <div className="ai-section statistics-section">
          <h4>
            Error Statistics
            <div className="analysis-controls">
              {isUsingCachedData && (
                <span className="cache-indicator" title="Data cached for better performance">
                  <i className="bi bi-clock-history"></i> Cached
                  <button
                    className="btn btn-sm btn-outline-secondary ml-2 refresh-btn"
                    onClick={handleRefreshAIAnalytics}
                    title="Refresh with latest data"
                    disabled={isLoadingAI}
                  >
                    <i className="bi bi-arrow-clockwise"></i> Refresh
                  </button>
                </span>
              )}
              <button
                className="btn btn-sm btn-outline-primary ml-2 regenerate-btn"
                onClick={handleRegenerateAIAnalytics}
                title="Regenerate AI analysis with fresh AI processing"
                disabled={isLoadingAI}
              >
                <i className="bi bi-stars"></i> Regenerate Analysis
              </button>
            </div>
          </h4>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{errorCategory?.scriptErrorCount || 0}</div>
              <div className="stat-label">Script Errors</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{errorCategory?.applicationErrorCount || 0}</div>
              <div className="stat-label">Application Errors</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{errorCategory?.environmentErrorCount || 0}</div>
              <div className="stat-label">Environment Errors</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{errorCategory?.dataErrorCount || 0}</div>
              <div className="stat-label">Data Errors</div>
            </div>
          </div>
        </div>

        {/* Error Groups */}
        <div className="ai-section error-groups-section">
          <h4>Error Groups Analysis</h4>
          <div className="error-groups-list">
            {errorGroups.map((group) => (
              <div key={group.groupId} className="error-group-card">
                <div className="group-header">
                  <div className="group-title">
                    <h5>{group.groupName}</h5>
                    <div className="group-badges">
                      <span className={`error-type-badge ${group.errorCategory.toLowerCase().replace(' ', '-')}`}>
                        {group.errorCategory}
                      </span>
                    </div>
                  </div>
                  <div className="group-stats">
                    <span className="affected-count">{group.affectedTransactionCount} transactions affected</span>
                  </div>
                </div>

                <div className="group-content">
                  <div className="group-details">
                    <div className="detail-item">
                      <strong>Error Pattern:</strong> {group.errorPattern}
                    </div>
                    {group.realErrorMessage && (
                      <div className="detail-item">
                        <strong>Error Message:</strong> {group.realErrorMessage}
                      </div>
                    )}
                    <div className="detail-item">
                      <strong>Root Cause:</strong> {group.rootCause}
                    </div>
                    <div className="detail-item">
                      <strong>Error Category:</strong>
                      <span className="category-help-text">
                        (You can update to improve AI categorization)
                      </span>
                      <div className="category-update-wrapper">
                        <select
                          className={`category-dropdown ${updatingCategories.has(group.groupId) ? 'updating' : ''}`}
                          value={mapAICategoryToPredefined(group.errorCategory) || group.errorCategory}
                          onChange={(e) => handleCategoryUpdate(group.groupId, e.target.value, group.errorCategory, group.realErrorMessage, group.errorPattern)}
                          disabled={updatingCategories.has(group.groupId)}
                        >
                          {/* Show current category if it's not mappable to predefined list */}
                          {!mapAICategoryToPredefined(group.errorCategory) && (
                            <option key={group.errorCategory} value={group.errorCategory}>
                              {group.errorCategory} (Current)
                            </option>
                          )}
                          {errorCategories.map(category => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                        {updatingCategories.has(group.groupId) && (
                          <div className="updating-indicator">
                            <i className="bi bi-arrow-repeat spin"></i>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="recommendations">
                    <h6>Recommended Actions:</h6>
                    <ul>
                      {group.recommendedActions.map((action, idx) => (
                        <li key={idx}>{action}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="common-keywords">
                    <h6>Common Error Keywords:</h6>
                    <div className="keywords-list">
                      {group.commonErrorKeywords.map((keyword, idx) => (
                        <span key={idx} className="keyword-chip">{keyword}</span>
                      ))}
                    </div>
                  </div>

                  <div className="affected-transactions">
                    <h6>Affected Transaction IDs:</h6>
                    <div className="transaction-ids">
                      {group.affectedTransactionIds.slice(0, 5).map((id) => (
                        <span
                          key={id}
                          className="transaction-id-chip"
                          onClick={() => handleTransactionClick(id)}
                          style={{ cursor: 'pointer' }}
                          title="Click to view transaction details"
                        >
                          {id.substring(0, 8)}...
                        </span>
                      ))}
                      {group.affectedTransactionIds.length > 5 && (
                        <span className="more-transactions">+{group.affectedTransactionIds.length - 5} more</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="execution-details-container">
        <LoadingSpinner text="Loading execution details..." />
      </div>
    );
  }

  if (!execution) {
    return (
      <div className="execution-details-container">
        <div className="error-message">
          <h2>Execution not found</h2>
          <button className="btn btn-primary" onClick={() => history.push('/jobsqueue')}>
            Back to Jobs Queue
          </button>
        </div>
      </div>
    );
  }

  const statusCounts = getStatusCounts();
  const totalTransactions = getTotalTransactions();

  return (
    <div className="execution-details-container">
      <div className="execution-header">
        <div className="execution-info">
          <h2>
            Execution: {execution.executionname || executionid}
          </h2>
          <div className="execution-metadata">
            <span className="metadata-item">
              <strong>Status:</strong> {execution.overall_status || execution.status}
            </span>
            <span className="metadata-item">
              <strong>Total Transactions:</strong> {totalTransactions} (excluding resubmitted)
            </span>
            <span className="metadata-item">
              <strong>Total Test-cases:</strong> {getTotalIterations()}
            </span>
            <span className="metadata-item">
              <strong>Created:</strong> {execution.createddate}
            </span>
          </div>
          {/* Add report icons for playwright executions */}
          {renderExecutionReports()}
        </div>
      </div>

      <div className="execution-content">
        {/* Summary Section with Pie Chart and Top Failures */}
        <div className="summary-section">
          <div className="row">
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="card">
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1rem', paddingBottom: '1rem' }}>
                  <h4 style={{ margin: 0 }}>
                    {chartView === 'transactions' ? 'Transaction Status Overview' : 'Test-cases Status Overview'}
                  </h4>
                  <div 
                    style={{ 
                      display: 'inline-flex', 
                      backgroundColor: '#f1f3f5',
                      borderRadius: '8px',
                      padding: '4px',
                      gap: '4px',
                      position: 'relative'
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setChartView('transactions')}
                      title="View transaction-level status"
                      style={{
                        padding: '8px 20px',
                        fontSize: '14px',
                        fontWeight: chartView === 'transactions' ? '600' : '500',
                        border: 'none',
                        borderRadius: '6px',
                        backgroundColor: chartView === 'transactions' ? '#ffffff' : 'transparent',
                        color: chartView === 'transactions' ? '#1a202c' : '#6b7280',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: chartView === 'transactions' ? '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)' : 'none',
                        position: 'relative',
                        zIndex: chartView === 'transactions' ? 2 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (chartView !== 'transactions') {
                          e.currentTarget.style.color = '#1a202c';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (chartView !== 'transactions') {
                          e.currentTarget.style.color = '#6b7280';
                        }
                      }}
                    >
                      <i className="bi bi-list-task" style={{ marginRight: '6px' }}></i>
                      Transactions
                    </button>
                    <button
                      type="button"
                      onClick={() => setChartView('iterations')}
                      title="View test-case level status"
                      style={{
                        padding: '8px 20px',
                        fontSize: '14px',
                        fontWeight: chartView === 'iterations' ? '600' : '500',
                        border: 'none',
                        borderRadius: '6px',
                        backgroundColor: chartView === 'iterations' ? '#ffffff' : 'transparent',
                        color: chartView === 'iterations' ? '#1a202c' : '#6b7280',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: chartView === 'iterations' ? '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)' : 'none',
                        position: 'relative',
                        zIndex: chartView === 'iterations' ? 2 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (chartView !== 'iterations') {
                          e.currentTarget.style.color = '#1a202c';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (chartView !== 'iterations') {
                          e.currentTarget.style.color = '#6b7280';
                        }
                      }}
                    >
                      <i className="bi bi-card-checklist" style={{ marginRight: '6px' }}></i>
                      Test-cases
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  {chartView === 'transactions' && totalTransactions > 0 && (
                    <Pie
                      data={getPieChartData()}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'bottom',
                            labels: {
                              padding: 20,
                              usePointStyle: true,
                            }
                          },
                          tooltip: {
                            callbacks: {
                              label: function (context) {
                                const percentage = ((context.parsed / totalTransactions) * 100).toFixed(1);
                                return `${context.label}: ${percentage}%`;
                              }
                            }
                          }
                        }
                      }}
                      height={300}
                    />
                  )}
                  {chartView === 'iterations' && getTotalIterations() > 0 && (
                    <Pie
                      data={getIterationPieChartData()}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'bottom',
                            labels: {
                              padding: 20,
                              usePointStyle: true,
                            }
                          },
                          tooltip: {
                            callbacks: {
                              label: function (context) {
                                const totalIterations = getTotalIterations();
                                const percentage = ((context.parsed / totalIterations) * 100).toFixed(1);
                                return `${context.label}: ${percentage}%`;
                              }
                            }
                          }
                        }
                      }}
                      height={300}
                    />
                  )}
                  {chartView === 'transactions' && totalTransactions === 0 && (
                    <div className="text-center py-5 text-muted">
                      <p>No transaction data available</p>
                    </div>
                  )}
                  {chartView === 'iterations' && getTotalIterations() === 0 && (
                    <div className="text-center py-5 text-muted">
                      <p>No test-case data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Top Most Failures alongside pie chart */}
            <div className="col-lg-6 col-md-6 col-sm-12">
              {topFailures.length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <h4>Top Most Failures ({topFailures.length} patterns)</h4>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: '50%' }}>Error Pattern</th>
                            <th style={{ width: '15%' }} className="text-center">Count</th>
                            <th style={{ width: '35%' }}>Affected Transactions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topFailures.slice(0, 8).map((failure, index) => (
                            <tr key={index}>
                              <td>
                                <div
                                  className="error-pattern-multiline"
                                  title={failure.originalMessage}
                                >
                                  {failure.displayMessage}
                                </div>
                              </td>
                              <td className="text-center">
                                <span className="badge badge-danger">
                                  {failure.count}
                                </span>
                              </td>
                              <td>
                                <div className="transaction-links-compact">
                                  {failure.transactions.slice(0, 4).map((transaction, txIndex) => (
                                    <button
                                      key={txIndex}
                                      className="btn btn-link btn-sm p-0 transaction-link-compact"
                                      onClick={() => handleTransactionClick(transaction.transactionid)}
                                      title={`View details for ${transaction.transactionid}`}
                                    >
                                      {transaction.transactionid.substring(0, 8)}...
                                    </button>
                                  ))}
                                  {failure.transactions.length > 4 && (
                                    <span className="more-transactions-compact">
                                      +{failure.transactions.length - 4} more
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content with Tabs */}
        <div className="analytics-and-errors-section">
          <div className="row">
            <div className="col-12">
              <div className="tabbed-content">
                {/* Tab Navigation */}
                <div className="tab-navigation">
                  <button
                    className={`tab-button ${activeTab === 'transactions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('transactions')}
                  >
                    Transaction Details
                  </button>
                  <button
                    className={`tab-button ${activeTab === 'ai-analysis' ? 'active' : ''}`}
                    onClick={handleAIAnalysisTabClick}
                  >
                    AI Analysis <small>(Beta)</small>
                    {isLoadingAI && <span className="loading-dot">⟳</span>}
                  </button>
                </div>

                {/* Tab Content */}
                <div className="tab-content">
                  {activeTab === 'transactions' && renderTransactionDetailsTab()}
                  {activeTab === 'ai-analysis' && renderAIAnalysisTab()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  addNewNotification: (message, category) =>
    dispatch(addNewNotification(message, category)),
});

export default connect(null, mapDispatchToProps)(ExecutionDetails);